import {
  ChangeDetectorRef,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IFilter } from 'src/app/shared/types/filters';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { _HttpClient } from '@delon/theme';
@Component({
  selector: 'app-filter-field',
  standalone: false,
  templateUrl: './filter-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterFieldComponent implements OnInit, OnDestroy {
  @Input() item!: IFilter;
  @Input() fc!: FormControl;
  loading = false;
  loadingMore = false;
  hasMore = true;
  pageSize = 20;
  private supportsPaging = false;
  private destroy$ = new Subject<void>();
  private currentSkip = 0;
  optionList: any[] = [];

  private isUrlSelectFilter(
    item: IFilter
  ): item is IFilter & {
    type: 'select';
    url: string;
    keyField: string;
    valueField: string;
  } {
    return (
      item.type === 'select' &&
      'url' in item &&
      !!item.url &&
      'keyField' in item &&
      !!item.keyField &&
      'valueField' in item &&
      !!item.valueField
    );
  }

  ngOnInit(): void {
    if (!this.isUrlSelectFilter(this.item)) {
      return;
    }

    this.supportsPaging = /\$skip=\d+/i.test(this.item.url);
    this.pageSize = this.getPageSizeFromUrl();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(private http: _HttpClient, private cdr: ChangeDetectorRef) {}

  private buildPagedUrl(skip: number): string {
    if (!this.isUrlSelectFilter(this.item)) return '';
    const baseUrl = this.item.url;

    if (this.supportsPaging) {
      return baseUrl.replace(/\$skip=\d+/i, `$skip=${skip}`);
    }

    return baseUrl;
  }

  private getPageSizeFromUrl(): number {
    if (!this.isUrlSelectFilter(this.item)) return this.pageSize;
    const baseUrl = this.item.url;
    const matchedTop = baseUrl.match(/\$top=(\d+)/i);
    const parsed = Number(matchedTop?.[1] ?? 0);
    return parsed > 0 ? parsed : this.pageSize;
  }

  private mapOptions(data: any): { key: any; label: any }[] {
    if (!this.isUrlSelectFilter(this.item)) return [];
    const keyField = this.item.keyField;
    const valueField = this.item.valueField;
    const rows = Array.isArray(data?.value) ? data.value : [];
    return rows
      .map((e: any) => ({
        key: e[keyField],
        label: e[valueField]
      }))
      .filter(
        (option: { key: any; label: any }) =>
          option.key !== undefined && option.label !== undefined
      );
  }

  private getOptions(skip: number) {
    const url = this.buildPagedUrl(skip);
    return this.http.get(url);
  }

  private loadOptions(reset: boolean): void {
    if (!this.isUrlSelectFilter(this.item)) return;
    if (!reset && !this.supportsPaging) return;
    if (!reset && (this.loading || this.loadingMore || !this.hasMore)) return;

    const skip = reset ? 0 : this.currentSkip;
    if (reset) {
      this.loading = true;
      this.hasMore = true;
      this.currentSkip = 0;
      this.optionList = [];
    } else {
      this.loadingMore = true;
    }

    this.getOptions(skip)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          const options = this.mapOptions(data);
          this.optionList = reset
            ? options
            : [...this.optionList, ...options];

          this.currentSkip = this.optionList.length;
          this.hasMore = this.supportsPaging && options.length >= this.pageSize;
          this.loading = false;
          this.loadingMore = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.loadingMore = false;
          this.cdr.detectChanges();
        }
      });
  }

  onOpenChange(value: boolean): void {
    if (value) {
      this.loadOptions(true);
    }
  }

  onScrollToBottom(): void {
    this.loadOptions(false);
  }
}
